import React from 'react';

import {
  Switch,
  Route,
  RouteProps,
  RouteChildrenProps,
  RouteComponentProps,
} from 'react-router-dom';

import { Miss, useServerData } from '@xingternal/hops';

import AuthenticatedRoute from '@/components/AuthenticatedRoute';
import { RecommendedCompanies } from '@/pages/RecommendedCompanies';
import { FollowedCompanies } from '@/pages/FollowedCompanies';
import { ManagedCompanies } from '@/pages/ManagedCompanies';
import { RecentlyClaimedCompanies } from '@/pages/RecentlyClaimedCompanies';
import { MostVisitedCompanies } from '@/pages/MostVisitedCompanies';
import { LoggedOutLandingPage } from '@/pages/LoggedOutLandingPage';
import { Industries } from '@/pages/Industries';
import { IndustryByCompanies } from '@/pages/IndustryByCompanies';
import { Breadcrumbs } from '@/components/Breadcrumbs';

import { ROUTES } from './routes';
import { getIndustryName } from '@/utils/url';
import { useIntl } from 'react-intl';

export type IndustryUrlParam = { industry: string };
type NameIndustry = ({ industry }: IndustryUrlParam) => string;

type CompaniesRoute = {
  onlyAuthenticated: Boolean;
  path: string;
  exact: Pick<RouteProps, 'exact'>['exact'];
  Component: React.FC<RouteChildrenProps>;
  name?: string | NameIndustry;
  hasCrumbs: boolean;
};

export type BreadcrumbsType = Pick<CompaniesRoute, 'name' | 'path'>;

const Router = () => {
  const { authenticated: loggedIn } = useServerData();
  const intl = useIntl();

  const routes: CompaniesRoute[] = [
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.companies,
      Component: loggedIn ? RecommendedCompanies : LoggedOutLandingPage,
      name: loggedIn ? 'Recommended Companies' : 'SE_LABEL_COMPANIES',
      hasCrumbs: !loggedIn,
    },
    {
      onlyAuthenticated: true,
      exact: true,
      path: ROUTES.followedCompanies,
      Component: FollowedCompanies,
      hasCrumbs: false,
    },
    {
      onlyAuthenticated: true,
      exact: true,
      path: ROUTES.managedCompanies,
      Component: ManagedCompanies,
      hasCrumbs: false,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.mostVisitedCompanies,
      Component: MostVisitedCompanies,
      name: 'CP_100_MOST_CLICKED_COMPANIES',
      hasCrumbs: !loggedIn,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.recentlyClaimedCompanies,
      Component: RecentlyClaimedCompanies,
      name: 'CP_NEW_EMPLOYERS',
      hasCrumbs: !loggedIn,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.industries,
      Component: Industries,
      name: 'CP_200_INDUSTRIES',
      hasCrumbs: !loggedIn,
    },
    {
      onlyAuthenticated: false,
      exact: false,
      path: ROUTES.industry,
      Component: IndustryByCompanies,
      name: ({ industry }) => getIndustryName({ intl, id: industry }),
      hasCrumbs: !loggedIn,
    },
  ];

  return (
    <Switch>
      {routes.map(
        ({ onlyAuthenticated, exact, path, Component, hasCrumbs }) => {
          const RouteComponent = onlyAuthenticated ? AuthenticatedRoute : Route;

          return (
            <RouteComponent
              key={path}
              exact={exact}
              path={path}
              render={(props: RouteComponentProps) => (
                <>
                  {hasCrumbs && <Breadcrumbs routes={routes} {...props} />}
                  <Component {...props} />
                </>
              )}
            ></RouteComponent>
          );
        },
      )}
      <Miss />
    </Switch>
  );
};
export { Router };
